'use client';

import { c as _c } from "react/compiler-runtime";
import type { ActiveWallet, GetPositionsRow } from '@morfi/backend-sdk-v2/client';
import { hashDiagnosticEntity, quoteOutcomeBuyForQuoteBudget, quoteOutcomeSellExactIn, quoteOutcomeSellFeeExcludedExactIn, useActiveWallet, useAuth, useGetMarketField, useMarketConfig, useSwapTrade, useTurnkeySigningDeps } from '@morfi/backend-sdk-v2/client';
import type { MarketStatusString } from '@morfi/contract-sdk';
import { applySlippage, d6ToUsdc, usdcToD6 } from '@morfi/contract-sdk';
import { BPS_DENOMINATOR, checkedOutcomePoolQuoteContextFromSnapshot, effectiveExecutionPriceD9, type OutcomeSellQuote, resolveOutcomePoolQuoteVenue, splitFloorFee } from '@morfi/market-math';
import BN from 'bn.js';
import { useTranslations } from 'next-intl';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/app-tabs';
import { Icon } from '@/components/ui/icon';
import { ArrowDataTransferVerticalIcon, RepeatIcon } from '@/components/ui/icon/glyphs';
import { Panel, PanelContent } from '@/components/ui/panel';
import { DetailWalletPicker } from '@/components/wallet/detail-wallet-picker';
import { D6_SCALE, USDC_DUST_THRESHOLD_D6 } from '@/config/solana';
import { TOAST_IDS } from '@/config/toast-ids';
import { useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import { useMarketPositions, useOwnedMarketPositionScope } from '@/hooks/positions/use-market-positions';
import { useUserMarketPortfolioPositions } from '@/hooks/positions/user-portfolio/selectors';
import type { BuyTradeEstimate, SellTradeEstimate, TradeEstimateParams } from '@/hooks/trading/use-trade-estimate';
import { useTradeEstimate } from '@/hooks/trading/use-trade-estimate';
import { useAllowRapidTrades } from '@/hooks/use-allow-rapid-trades';
import { checkGate } from '@/lib/analytics';
import { useFormattedCurrency, useQuickAmounts } from '@/lib/currency';
import { useActiveNetwork } from '@/lib/network/use-active-network';
import { claimRightsMintAddress } from '@/lib/solana/claim-rights-mint';
import { logger } from '@/lib/telemetry/logger';
import { positionAccountingDtoFromRow } from '@/lib/trading/position-accounting-dto';
import { confettiOriginFromElement } from '@/lib/ui/confetti';
import { showToast } from '@/lib/ui/toast';
import { cn } from '@/lib/ui/utils';
import { isDust } from '@/lib/validation/dust';
import { getExternalWalletProvider } from '@/lib/wallet/external-wallet-provider';
import { useRequiredTradeExecutor } from '@/providers/trade-executor-provider';
import type { OnchainTradeData } from '@/queries/onchain-trade-data';
import { useTradeBalances } from '@/queries/trade-balances';
import { useChartSettingsStore } from '@/stores/chart-settings';
import { isTradeSubmissionBlocked } from '@/stores/trade-submission-lock';
import { AnimatedAmountInput } from './animated-amount-input';
import { OutcomePills } from './outcome-pills';
import { ReceiveSharesRow } from './receive-shares-row';
import { TradeDetails } from './trade-details';
import { TradeFormTransition } from './trade-form-transition';
import { deriveTradePanelState, type TradePanelValidationReason } from './trade-panel-state';
import { TradeSettingsDialog } from './trade-settings-dialog';

// ── Types ────────────────────────────────────────────────────────────────────

export interface OutcomeInfo {
  index: number;
  label: string;
  mint: string;
  /** Optional outcome image URL from backend. */
  imageUrl?: string | undefined;
}

/** i18n validation key suffixes for non-tradeable market statuses. */
type NonTradeableKey = 'market_created' | 'market_backing' | 'market_resolved' | 'market_graduated';
const NON_TRADEABLE_STATUS_KEYS: Readonly<Record<string, NonTradeableKey>> = {
  created: 'market_created',
  backing: 'market_backing',
  resolved: 'market_resolved',
  graduated: 'market_graduated'
};
function getNonTradeableKey(status: string): NonTradeableKey | undefined {
  return NON_TRADEABLE_STATUS_KEYS[status];
}
type WalletBalanceState = Parameters<typeof deriveTradePanelState>[0]['walletBalance'];
interface WalletBalanceStateInput {
  needsLogin: boolean;
  isBuy: boolean;
  hasUsdcBalance: boolean;
  hasTradeBalances: boolean;
  queryError: boolean;
  queryPending: boolean;
  queryPlaceholder: boolean;
  queryFetching: boolean;
  queryStale: boolean;
  exactSellBalanceAvailable: boolean;
}
function deriveWalletBalanceState(input: WalletBalanceStateInput): WalletBalanceState {
  if (input.needsLogin) return {
    status: 'not_required'
  };
  if (input.queryError) return {
    status: 'unavailable',
    reason: 'request_failed'
  };
  if (input.isBuy) {
    if (!input.hasUsdcBalance || input.queryPending) return {
      status: 'loading'
    };
    // The quote-token balance belongs to the wallet, not the outcome. Keep
    // Buy ready while the selected outcome's ATA snapshot preloads in the
    // background; transaction construction safely verifies those accounts.
    if (input.queryPlaceholder) return {
      status: 'ready'
    };
    return input.queryFetching || input.queryStale ? {
      status: 'refreshing'
    } : {
      status: 'ready'
    };
  }
  if (!input.hasTradeBalances || input.queryPending || input.queryPlaceholder) {
    return {
      status: 'loading'
    };
  }
  if (input.queryFetching || input.queryStale) return {
    status: 'refreshing'
  };
  return input.exactSellBalanceAvailable ? {
    status: 'ready'
  } : {
    status: 'unavailable',
    reason: 'required_assets_missing'
  };
}
export interface TradeFormProps {
  marketAddress: string;
  /** Canonical lifecycle + executable pool state used by every SDK quote. */
  tradeData?: OnchainTradeData;
  /** Market display name — featured in the realised-PnL sell share text. */
  marketTitle?: string;
  outcomes: OutcomeInfo[];
  selectedOutcomeIndex?: number;
  /** Current market status from the backend API. When absent, trading is allowed (backwards compat). */
  marketStatus?: MarketStatusString;
  /**
   * Opt-in: allow trading a `graduated` market via the AMM path. The default
   * markets-detail callsite leaves this `false`, so `graduated` stays disabled
   * there (the right column shows the TokenCta link, not a trade form). The
   * `/winners` token page sets this `true` so the AMM Buy/Sell form is live for
   * the graduated winning token. Never re-enables bonding-curve trading.
   */
  allowGraduatedAmm?: boolean;
  /** Lock the form to a single outcome — the selector renders as a static label
   *  (no dropdown) and the Swap tab is hidden. The `/winners` token page sets
   *  this so the form trades only the graduated winning token. */
  lockOutcome?: boolean;
  /**
   * True when the on-chain state failed to refresh. Exact quotes and submission
   * are blocked until fresh state is available.
   */
  marketDataStale?: boolean;
  /** True before the first identity-checked pool snapshot is available. */
  marketDataPending?: boolean;
  /**
   * True while a newer exact chain snapshot is being reconciled. The last
   * coherent quote remains visible, but submission is blocked until it settles.
   */
  marketDataRefreshing?: boolean;
  /** Retries the parent-owned pool snapshot query after a genuine failure. */
  onRetryMarketData?: () => void;
  /** Called when the user selects a different outcome. */
  onOutcomeChange?: (index: number) => void;
  /** Whether the user currently has non-dust holdings in this market.
   *  When false, a successful buy will invalidate the SDK holdings cache
   *  so the Holdings tab appears immediately. When true (or omitted),
   *  realtime CDC handles updates and we skip invalidation. */
  hasHoldings?: boolean;
}

// ── Constants ────────────────────────────────────────────────────────────────

const BC_MAX_SLIPPAGE_BPS = 2000;
const AMM_MAX_SLIPPAGE_BPS = 500;
function integerWireD6(value: string | undefined): bigint | null {
  if (value === undefined || !/^\d+$/.test(value)) return null;
  return BigInt(value);
}
function minimumBigInt(left: bigint, right: bigint): bigint {
  return left < right ? left : right;
}
function floorDisplayAmount(value: number, decimals: number): number {
  if (!Number.isFinite(value) || value <= 0) return 0;
  const scale = 10 ** Math.max(0, decimals);
  return Math.floor(value * scale) / scale;
}
function boundedEditableUsdcFromD6(valueD6: bigint): number | null {
  if (valueD6 < BigInt(0)) return null;
  const scale = BigInt(D6_SCALE);
  const whole = valueD6 / scale;
  if (whole > BigInt(Number.MAX_SAFE_INTEGER)) return null;
  return Number(whole) + Number(valueD6 % scale) / D6_SCALE;
}
function formatOutcomeTokenD6(valueD6: bigint): string {
  const scale = BigInt(D6_SCALE);
  const negative = valueD6 < BigInt(0);
  const magnitude = negative ? -valueD6 : valueD6;
  const whole = magnitude / scale;
  const fraction = (magnitude % scale).toString().padStart(6, '0').replace(/0+$/, '');
  const unsigned = fraction.length === 0 ? whole.toString() : `${whole.toString()}.${fraction}`;
  return negative ? `-${unsigned}` : unsigned;
}
function effectiveExecutionPriceD9OrNull(quoteAmountD6: bigint, outcomeAmountD6: bigint): bigint | null {
  const price = effectiveExecutionPriceD9(quoteAmountD6, outcomeAmountD6);
  return price.available ? price.value : null;
}
function isDustAmountD6(valueD6: bigint): boolean {
  const magnitude = valueD6 < BigInt(0) ? -valueD6 : valueD6;
  return magnitude < BigInt(USDC_DUST_THRESHOLD_D6);
}
interface SellDisplayEstimate {
  grossQuoteAmountD6: bigint;
  netUsdcReceivedD6: bigint;
  baseFeeD6: bigint;
  prizePoolFeeD6: bigint;
  penaltyFeeD6: bigint;
  outcomeInputFeeD6: bigint;
  quoteOutputCreatorFeeD6: bigint;
  isPenalty: boolean;
  marketPhase: 'bonding_curve' | 'amm';
}

/** Display-only projection of one exact SDK sell quote. */
function sellDisplayEstimateFromQuote(quote: OutcomeSellQuote): SellDisplayEstimate {
  if (quote.venue === 'bonding_curve') {
    return {
      grossQuoteAmountD6: quote.grossQuoteOutputD6,
      netUsdcReceivedD6: quote.netQuoteOutputD6,
      baseFeeD6: quote.sellBaseFeeD6,
      prizePoolFeeD6: quote.prizePoolFeeD6,
      penaltyFeeD6: quote.sellPenaltyFeeD6,
      outcomeInputFeeD6: BigInt(0),
      quoteOutputCreatorFeeD6: BigInt(0),
      isPenalty: quote.penaltyApplied,
      marketPhase: 'bonding_curve'
    };
  }
  return {
    grossQuoteAmountD6: quote.curveOutputAmountD6,
    netUsdcReceivedD6: quote.outputAmountD6,
    baseFeeD6: BigInt(0),
    prizePoolFeeD6: BigInt(0),
    penaltyFeeD6: BigInt(0),
    outcomeInputFeeD6: quote.tradeFeeD6 + (quote.creatorFeeOnInput ? quote.creatorFeeD6 : BigInt(0)),
    quoteOutputCreatorFeeD6: quote.creatorFeeOnInput ? BigInt(0) : quote.creatorFeeD6,
    isPenalty: false,
    marketPhase: 'amm'
  };
}
function sellDisplayEstimateFromEstimate(estimate: SellTradeEstimate): SellDisplayEstimate {
  return {
    grossQuoteAmountD6: BigInt(estimate.grossQuoteAmountD6.toString()),
    netUsdcReceivedD6: BigInt(estimate.expectedNetUsdcOutD6.toString()),
    baseFeeD6: BigInt(estimate.sellBaseFeeD6.toString()),
    prizePoolFeeD6: BigInt(estimate.prizePoolFeeD6.toString()),
    penaltyFeeD6: BigInt(estimate.penaltyFeeD6.toString()),
    outcomeInputFeeD6: BigInt(estimate.outcomeInputFeeD6.toString()),
    quoteOutputCreatorFeeD6: BigInt(estimate.quoteOutputCreatorFeeD6.toString()),
    isPenalty: estimate.isPenalty,
    marketPhase: estimate.marketPhase
  };
}

// ── Component ────────────────────────────────────────────────────────────────

/**
 * Internal trade form — `<TradeForm>` (panel-wrapped) and `<TradeFormBare>`
 * (no panel) both delegate to this component. The `bare` boolean was removed
 * from the public surface (spec 055 / US6); pick a layout wrapper instead.
 */
interface TradeFormModel {
  formContent: React.ReactNode;
  bare: boolean;
}

/**
 * The entire trade-form orchestration — outcome selection, BC/AMM mode
 * derivation, amount + slippage state, on-chain quote/estimate math, the
 * submit lifecycle, and the assembled `formContent` JSX. Kept as a hook in this
 * file (the money logic is moved verbatim, not rewritten) so every file-keyed
 * verified-FP exemption stays valid (refs, prefer-useReducer, no-event-handler,
 * set-state-in-effect, no-chain-state-updates, no-adjust-state-on-prop-change,
 * rerender-lazy-ref-init, the no-derived-state class). The component below is a
 * thin wrapper that drops the body under react-doctor/no-giant-component.
 */
function useTradeForm(props) {
  const $ = _c(136);
  const {
    marketAddress,
    tradeData,
    marketTitle,
    outcomes,
    selectedOutcomeIndex,
    marketStatus,
    allowGraduatedAmm: t0,
    lockOutcome: t1,
    marketDataStale: t2,
    marketDataPending: t3,
    marketDataRefreshing: t4,
    bare
  } = props;
  const allowGraduatedAmm = t0 === undefined ? false : t0;
  const lockOutcome = t1 === undefined ? false : t1;
  const marketDataStale = t2 === undefined ? false : t2;
  const marketDataPending = t3 === undefined ? false : t3;
  const marketDataRefreshing = t4 === undefined ? false : t4;
  const {
    onOutcomeChange,
    onRetryMarketData
  } = props;
  const t = useTranslations("trade");
  const {
    formatBalance,
    formatD6,
    formatD9,
    currencySymbol,
    exchangeRate,
    currencyDecimals,
    toUsdc
  } = useFormattedCurrency();
  const {
    activeWallet
  } = useActiveWallet();
  const {
    user,
    isAuthenticated,
    isLoading: isAuthLoading
  } = useAuth();
  const hasAuthenticatedWallet = !isAuthLoading && isAuthenticated && activeWallet !== null;
  const needsLogin = !hasAuthenticatedWallet;
  const executor = useRequiredTradeExecutor();
  const allowRapidTrades = useAllowRapidTrades();
  const signingDeps = useTurnkeySigningDeps(getExternalWalletProvider);
  const [animateFormChanges, setAnimateFormChanges] = useState(false);
  const [direction, setDirection] = useState("buy");
  const [buyAmountStr, setBuyAmountStr] = useState("");
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      value: "",
      sellPercentage: null
    };
    $[0] = t5;
  } else {
    t5 = $[0];
  }
  const [sellAmount, setSellAmount] = useState(t5);
  const amountStr = direction === "buy" ? buyAmountStr : sellAmount.value;
  let t6;
  if ($[1] !== marketAddress) {
    t6 = s => s.tradeDirectionRequest[marketAddress] ?? null;
    $[1] = marketAddress;
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  const tradeDirRequest = useChartSettingsStore(t6);
  const lastSeenDirNonceRef = useRef(tradeDirRequest?.nonce ?? -1);
  const pendingMaxNonceRef = useRef(null);
  let t7;
  let t8;
  if ($[3] !== tradeDirRequest) {
    t7 = () => {
      if (tradeDirRequest === null) {
        return;
      }
      if (tradeDirRequest.nonce === lastSeenDirNonceRef.current) {
        return;
      }
      lastSeenDirNonceRef.current = tradeDirRequest.nonce;
      setAnimateFormChanges(true);
      setDirection(tradeDirRequest.direction);
      if (tradeDirRequest.amount === "max") {
        pendingMaxNonceRef.current = tradeDirRequest.nonce;
      }
    };
    t8 = [tradeDirRequest];
    $[3] = tradeDirRequest;
    $[4] = t7;
    $[5] = t8;
  } else {
    t7 = $[4];
    t8 = $[5];
  }
  useEffect(t7, t8);
  const [uncontrolledOutcomeIndex, setUncontrolledOutcomeIndex] = useState(selectedOutcomeIndex ?? 0);
  const outcomeIndex = selectedOutcomeIndex ?? uncontrolledOutcomeIndex;
  const sellPercentage = direction === "sell" && sellAmount.sellPercentage?.outcomeIndex === outcomeIndex ? sellAmount.sellPercentage.percentage : null;
  const sellAllSelected = sellPercentage === 100;
  let t9;
  if ($[6] !== direction) {
    t9 = function setAmountStr(value) {
      if (direction === "buy") {
        setBuyAmountStr(value);
      } else {
        setSellAmount({
          value,
          sellPercentage: null
        });
      }
    };
    $[6] = direction;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  const setAmountStr = t9;
  let t10;
  if ($[8] !== onOutcomeChange || $[9] !== selectedOutcomeIndex) {
    t10 = function setOutcomeIndex(index) {
      if (selectedOutcomeIndex === undefined) {
        setUncontrolledOutcomeIndex(index);
      }
      onOutcomeChange?.(index);
    };
    $[8] = onOutcomeChange;
    $[9] = selectedOutcomeIndex;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  const setOutcomeIndex = t10;
  let t11;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = new Set();
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  const unavailableStateLogKeysRef = useRef(t11);
  let t12;
  if ($[12] !== outcomeIndex) {
    t12 = outcome => outcome.index === outcomeIndex;
    $[12] = outcomeIndex;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  const selectedOutcome = outcomes.find(t12);
  const checkedSelectedPoolContext = tradeData !== undefined && selectedOutcome !== undefined ? checkedOutcomePoolQuoteContextFromSnapshot(tradeData, marketAddress, outcomeIndex, selectedOutcome.mint, marketDataStale) : null;
  const selectedPoolContext = checkedSelectedPoolContext?.available === true ? checkedSelectedPoolContext.value.context : null;
  const selectedPoolContextUnavailableReason = tradeData !== undefined && selectedOutcome === undefined ? "selected_outcome_missing" : checkedSelectedPoolContext?.available === false ? checkedSelectedPoolContext.reason : null;
  const selectedVenue = selectedPoolContext !== null ? resolveOutcomePoolQuoteVenue(selectedPoolContext) : null;
  const isAmm = selectedVenue?.available === true && selectedVenue.value === "cp_swap";
  let t13;
  if ($[14] !== marketAddress) {
    t13 = {
      pubkey: marketAddress
    };
    $[14] = marketAddress;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  const config = useMarketConfig(useGetMarketField(t13, "marketConfig"));
  const feeSplitBackersBps = config?.feeSplitBackersBps;
  let t14;
  if ($[16] !== outcomes) {
    t14 = outcomes.map(_temp);
    $[16] = outcomes;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  if ($[18] !== marketAddress) {
    t15 = {
      marketPubkey: marketAddress
    };
    $[18] = marketAddress;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  const outcomeColorClasses = useOutcomeColor(t14, t15);
  const outcomeColorByIndex = new Map();
  for (let position = 0; position < outcomes.length; position++) {
    const outcome_0 = outcomes[position];
    const color = outcomeColorClasses[position];
    if (outcome_0 !== undefined && color !== undefined) {
      outcomeColorByIndex.set(outcome_0.index, color);
    }
  }
  const selectedOutcomeColor = outcomeColorByIndex.get(outcomeIndex);
  let t16;
  if ($[20] !== outcomes || $[21] !== selectedOutcomeIndex) {
    t16 = () => {
      const first = outcomes.find(o_0 => o_0.index !== (selectedOutcomeIndex ?? 0));
      return first?.index ?? 1;
    };
    $[20] = outcomes;
    $[21] = selectedOutcomeIndex;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  const [swapToOutcomeIndex, setSwapToOutcomeIndex] = useState(t16);
  const swapToIndex = swapToOutcomeIndex === outcomeIndex ? outcomes.find(o_1 => o_1.index !== outcomeIndex)?.index ?? swapToOutcomeIndex : swapToOutcomeIndex;
  const swapToOutcome = outcomes.find(outcome_1 => outcome_1.index === swapToIndex);
  const [swapPctStr, setSwapPctStr] = useState("");
  const swapPct = Number.parseFloat(swapPctStr) || 0;
  let localAmount;
  let t17;
  if ($[23] !== amountStr || $[24] !== toUsdc) {
    localAmount = Number.parseFloat(amountStr) || 0;
    t17 = toUsdc(localAmount);
    $[23] = amountStr;
    $[24] = toUsdc;
    $[25] = localAmount;
    $[26] = t17;
  } else {
    localAmount = $[25];
    t17 = $[26];
  }
  const usdcAmount = t17;
  const requiresSellBalances = direction !== "buy";
  const isBondingCurve = selectedPoolContext?.poolModel === "bonding_curve";
  const network = useActiveNetwork();
  const selectedClaimRightsMint = isBondingCurve && selectedOutcome !== undefined ? claimRightsMintAddress(marketAddress, outcomeIndex, network) : undefined;
  const tradeBalancesQuery = useTradeBalances(activeWallet?.address, requiresSellBalances || isBondingCurve ? selectedOutcome?.mint : undefined, selectedClaimRightsMint);
  const tradeBalances = tradeBalancesQuery.data;
  const exactTradeBalances = tradeBalancesQuery.isPlaceholderData === true || tradeBalancesQuery.isError ? undefined : tradeBalances;
  const ownedPositionScope = useOwnedMarketPositionScope();
  let t18;
  if ($[27] !== marketAddress || $[28] !== ownedPositionScope.userId || $[29] !== ownedPositionScope.walletPubkeys) {
    t18 = {
      userId: ownedPositionScope.userId,
      walletPubkeys: ownedPositionScope.walletPubkeys,
      market: marketAddress
    };
    $[27] = marketAddress;
    $[28] = ownedPositionScope.userId;
    $[29] = ownedPositionScope.walletPubkeys;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  const {
    rows: ownedMarketPositions
  } = useMarketPositions(t18);
  const positionByOutcome = new Map();
  for (const r of ownedMarketPositions) {
    if (r.market === marketAddress && r.wallet === activeWallet?.address) {
      positionByOutcome.set(r.outcomeIndex, r);
    }
  }
  const t19 = user?.id ?? "";
  const t20 = activeWallet?.address ?? "";
  let t21;
  if ($[31] !== marketAddress || $[32] !== t19 || $[33] !== t20) {
    t21 = {
      userId: t19,
      wallet: t20,
      market: marketAddress
    };
    $[31] = marketAddress;
    $[32] = t19;
    $[33] = t20;
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  const {
    rows: portfolioDisplayRows,
    isPending: portfolioDisplayPending,
    isError: portfolioDisplayError
  } = useUserMarketPortfolioPositions(t21);
  const portfolioDisplayByOutcome = new Map(portfolioDisplayRows.map(_temp2));
  const hasReserves = selectedVenue?.available === true && !marketDataStale;
  const selectedPosition = positionByOutcome.get(outcomeIndex);
  const sellPositionAccounting = positionAccountingDtoFromRow(selectedPosition);
  const heldOutcomeD6 = integerWireD6(selectedPosition?.balance) ?? BigInt(0);
  const heldClaimRightsD6 = integerWireD6(selectedPosition?.claimRightsBalanceD6);
  const onchainOutcomeBalanceD6 = integerWireD6(exactTradeBalances?.outcomeRawBalance);
  const onchainClaimRightsBalanceD6 = selectedClaimRightsMint === undefined ? null : integerWireD6(exactTradeBalances?.claimRightsRawBalance);
  const exactSellBalanceAvailable = onchainOutcomeBalanceD6 !== null && (selectedPoolContext?.poolModel !== "bonding_curve" || onchainClaimRightsBalanceD6 !== null && (selectedPosition === undefined || heldClaimRightsD6 !== null));
  let maximumOutcomeAmountInD6 = BigInt(0);
  if (exactSellBalanceAvailable && onchainOutcomeBalanceD6 !== null) {
    maximumOutcomeAmountInD6 = minimumBigInt(heldOutcomeD6, onchainOutcomeBalanceD6);
    if (selectedPoolContext?.poolModel === "bonding_curve" && heldClaimRightsD6 !== null && onchainClaimRightsBalanceD6 !== null) {
      maximumOutcomeAmountInD6 = minimumBigInt(maximumOutcomeAmountInD6, minimumBigInt(heldClaimRightsD6, onchainClaimRightsBalanceD6));
    }
  }
  const isSellTab = direction === "sell";
  const tokenHoldingBalanceRaw = boundedEditableUsdcFromD6(maximumOutcomeAmountInD6) ?? 0;
  const bagSellEstimate = selectedPoolContext !== null && maximumOutcomeAmountInD6 > BigInt(0) && !marketDataStale ? quoteOutcomeSellExactIn({
    context: selectedPoolContext,
    outcomeAmountInD6: maximumOutcomeAmountInD6
  }) : null;
  const tokenHoldingSellValueD6 = bagSellEstimate?.available === true ? bagSellEstimate.value.venue === "bonding_curve" ? bagSellEstimate.value.grossQuoteOutputD6 : bagSellEstimate.value.curveOutputAmountD6 : BigInt(0);
  const tokenHoldingSellValueUsdcRaw = boundedEditableUsdcFromD6(tokenHoldingSellValueD6) ?? 0;
  const hasDustHoldings = isDustAmountD6(tokenHoldingSellValueD6);
  const tokenHoldingBalance = hasDustHoldings ? 0 : tokenHoldingBalanceRaw;
  const tokenHoldingSellValueUsdc = hasDustHoldings ? 0 : tokenHoldingSellValueUsdcRaw;
  const hasNoSellableHoldings = isSellTab && !needsLogin && exactSellBalanceAvailable && maximumOutcomeAmountInD6 <= BigInt(0);
  let t22;
  let t23;
  if ($[35] !== hasNoSellableHoldings || $[36] !== sellPercentage) {
    t22 = () => {
      if (sellPercentage === null || !hasNoSellableHoldings) {
        return;
      }
      setSellAmount(_temp3);
    };
    t23 = [hasNoSellableHoldings, sellPercentage];
    $[35] = hasNoSellableHoldings;
    $[36] = sellPercentage;
    $[37] = t22;
    $[38] = t23;
  } else {
    t22 = $[37];
    t23 = $[38];
  }
  useEffect(t22, t23);
  const sellAllFillLocal = isSellTab ? floorDisplayAmount(tokenHoldingSellValueUsdc * exchangeRate, currencyDecimals) : 0;
  const percentageOutcomeAmountD6 = sellPercentage === null ? BigInt(0) : maximumOutcomeAmountInD6 * BigInt(sellPercentage) / BigInt(100);
  const percentageSellQuote = sellPercentage === 100 ? bagSellEstimate : sellPercentage !== null && selectedPoolContext !== null && percentageOutcomeAmountD6 > BigInt(0) && !marketDataStale ? quoteOutcomeSellExactIn({
    context: selectedPoolContext,
    outcomeAmountInD6: percentageOutcomeAmountD6
  }) : null;
  const percentageSellDisplayEstimate = percentageSellQuote?.available === true ? sellDisplayEstimateFromQuote(percentageSellQuote.value) : null;
  const percentageSellValueD6 = percentageSellDisplayEstimate?.grossQuoteAmountD6 ?? BigInt(0);
  const percentageSellValueUsdc = boundedEditableUsdcFromD6(percentageSellValueD6) ?? 0;
  const t24 = percentageSellValueUsdc * exchangeRate;
  let t25;
  if ($[39] !== currencyDecimals || $[40] !== t24) {
    t25 = floorDisplayAmount(t24, currencyDecimals);
    $[39] = currencyDecimals;
    $[40] = t24;
    $[41] = t25;
  } else {
    t25 = $[41];
  }
  const percentageFillLocal = t25;
  const percentageQuoteReady = percentageSellDisplayEstimate !== null && !isDustAmountD6(percentageSellValueD6);
  const liveSellMaxReady = isSellTab && bagSellEstimate?.available === true && !hasDustHoldings;
  const quoteUsdcAmount = sellPercentage !== null ? percentageSellValueUsdc : liveSellMaxReady && usdcAmount > tokenHoldingSellValueUsdc ? tokenHoldingSellValueUsdc : usdcAmount;
  let estimateParams = null;
  const quoteInputsReady = quoteUsdcAmount > 0 && hasReserves && selectedPoolContext !== null;
  const quoteIsPublic = direction === "buy";
  if (quoteInputsReady && direction !== "swap" && (quoteIsPublic || !needsLogin)) {
    estimateParams = {
      direction: direction as 'buy' | 'sell',
      amountUsdc: quoteUsdcAmount,
      context: selectedPoolContext,
      maximumOutcomeAmountInD6,
      slippageBps: 100
    };
  }
  const estimate = useTradeEstimate(estimateParams);
  const maxSlippageBps = isAmm ? AMM_MAX_SLIPPAGE_BPS : BC_MAX_SLIPPAGE_BPS;
  const rawSlippageBps = estimate?.suggestedSlippageBps ?? 100;
  const effectiveSlippageBps = Math.min(rawSlippageBps, maxSlippageBps);
  const swapMutation = useSwapTrade();
  const isTradePending = executor.isTradePending || swapMutation.isPending;
  let t26;
  let t27;
  if ($[42] !== executor || $[43] !== swapMutation.isPending) {
    t26 = () => {
      executor.registerSwapPending(swapMutation.isPending);
      return () => {
        executor.registerSwapPending(false);
      };
    };
    t27 = [swapMutation.isPending, executor];
    $[42] = executor;
    $[43] = swapMutation.isPending;
    $[44] = t26;
    $[45] = t27;
  } else {
    t26 = $[44];
    t27 = $[45];
  }
  useEffect(t26, t27);
  const formRootRef = useRef(null);
  let t28;
  if ($[46] !== swapMutation) {
    t28 = () => {
      if (swapMutation.isSuccess) {
        swapMutation.reset();
      }
    };
    $[46] = swapMutation;
    $[47] = t28;
  } else {
    t28 = $[47];
  }
  let t29;
  if ($[48] !== swapMutation.isSuccess || $[49] !== swapMutation.reset) {
    t29 = [swapMutation.isSuccess, swapMutation.reset];
    $[48] = swapMutation.isSuccess;
    $[49] = swapMutation.reset;
    $[50] = t29;
  } else {
    t29 = $[50];
  }
  useEffect(t28, t29);
  const balance = tradeBalancesQuery.usdcBalance ?? 0;
  const isBuy = direction === "buy";
  const isSwap = direction === "swap";
  const checkedSwapToPoolContext = isSwap && tradeData !== undefined && swapToOutcome !== undefined ? checkedOutcomePoolQuoteContextFromSnapshot(tradeData, marketAddress, swapToIndex, swapToOutcome.mint, marketDataStale) : null;
  const swapToPoolContext = checkedSwapToPoolContext?.available === true ? checkedSwapToPoolContext.value.context : null;
  const swapToPoolContextUnavailableReason = isSwap && tradeData !== undefined && swapToOutcome === undefined ? "selected_outcome_missing" : checkedSwapToPoolContext?.available === false ? checkedSwapToPoolContext.reason : null;
  const blockedWhilePending = isSwap && isTradePending || executor.isBuySellBlocked;
  const blockForRefresh = !allowRapidTrades && marketDataRefreshing;
  const isGraduatedAmm = allowGraduatedAmm && marketStatus === "graduated";
  const graduatedAmmSubmitColor = isBuy ? "bg-outcome-green-fill hover:bg-outcome-green-outline text-outcome-green-text" : "bg-outcome-red-fill hover:bg-outcome-red-outline text-outcome-red-text";
  const buyEstimate = estimate?.direction === "buy" ? estimate as BuyTradeEstimate : null;
  const sellEstimate = estimate?.direction === "sell" ? estimate as SellTradeEstimate : null;
  const outcomeBalances = {};
  for (const o_2 of outcomes) {
    const displayRow = portfolioDisplayByOutcome.get(o_2.index);
    const valueD6 = displayRow === undefined ? BigInt(0) : displayRow.displayMark.currentValueD6;
    const cleanD6 = valueD6 !== null && isDustAmountD6(valueD6) ? BigInt(0) : valueD6;
    outcomeBalances[o_2.index] = needsLogin || portfolioDisplayPending || portfolioDisplayError || cleanD6 === null ? formatD6("0") : formatD6(cleanD6.toString());
  }
  const visibleOutcomeBalances = hasAuthenticatedWallet ? outcomeBalances : undefined;
  const exactBagSellDisplayEstimate = bagSellEstimate?.available === true ? sellDisplayEstimateFromQuote(bagSellEstimate.value) : null;
  const exactInputSellDisplayEstimate = sellEstimate !== null ? sellDisplayEstimateFromEstimate(sellEstimate) : null;
  const sellExceedsHoldings = sellPercentage === null && !isBuy && !isSwap && localAmount > 0 && tokenHoldingSellValueUsdc > 0 && (usdcAmount > tokenHoldingSellValueUsdc || sellEstimate !== null && onchainOutcomeBalanceD6 !== null && BigInt(sellEstimate.tokensNeededD6.toString()) > onchainOutcomeBalanceD6);
  const displayedSellEstimate = sellPercentage !== null ? percentageSellDisplayEstimate : sellExceedsHoldings ? exactBagSellDisplayEstimate : exactInputSellDisplayEstimate;
  const displayedAmountStr = sellPercentage !== null && percentageSellDisplayEstimate !== null ? String(percentageFillLocal) : liveSellMaxReady && sellExceedsHoldings ? String(sellAllFillLocal) : amountStr;
  const swapOutcomeAmountInD6 = swapPct > 0 ? maximumOutcomeAmountInD6 * BigInt(swapPct) / BigInt(100) : BigInt(0);
  let t30;
  bb0: {
    if (!isSwap || swapPct <= 0 || tokenHoldingSellValueUsdc <= 0 || tradeData === undefined || selectedPoolContext === null || swapToPoolContext === null || swapToOutcome === undefined || marketDataStale) {
      t30 = null;
      break bb0;
    }
    const fromContext = selectedPoolContext;
    const toContext = swapToPoolContext;
    if (swapOutcomeAmountInD6 === BigInt(0)) {
      t30 = null;
      break bb0;
    }
    const sellResult = quoteOutcomeSellExactIn({
      context: fromContext,
      outcomeAmountInD6: swapOutcomeAmountInD6
    });
    if (!sellResult.available) {
      t30 = null;
      break bb0;
    }
    const sellNetD6 = sellResult.value.venue === "bonding_curve" ? sellResult.value.netQuoteOutputD6 : sellResult.value.outputAmountD6;
    if (sellNetD6 <= BigInt(0)) {
      t30 = null;
      break bb0;
    }
    const flooredSellProceedsD6 = applySlippage(new BN(sellNetD6.toString()), effectiveSlippageBps, "floor");
    const buyResult = quoteOutcomeBuyForQuoteBudget({
      context: toContext,
      maximumQuoteDebitD6: BigInt(flooredSellProceedsD6.toString())
    });
    if (!buyResult.available) {
      t30 = null;
      break bb0;
    }
    const sellGrossD6 = sellResult.value.venue === "bonding_curve" ? sellResult.value.grossQuoteOutputD6 : sellResult.value.curveOutputAmountD6;
    const sellBaseFeeD6 = sellResult.value.venue === "bonding_curve" ? sellResult.value.sellBaseFeeD6 : BigInt(0);
    const sellPrizePoolFeeD6 = sellResult.value.venue === "bonding_curve" ? sellResult.value.prizePoolFeeD6 : BigInt(0);
    const sellPenaltyFeeD6 = sellResult.value.venue === "bonding_curve" ? sellResult.value.sellPenaltyFeeD6 : BigInt(0);
    const buyFeeD6 = buyResult.value.venue === "bonding_curve" ? buyResult.value.buyFeeD6 : BigInt(0);
    const buyPrizePoolFeeD6 = buyResult.value.venue === "bonding_curve" ? buyResult.value.prizePoolFeeD6 : BigInt(0);
    const totalCostD6 = sellBaseFeeD6 + sellPrizePoolFeeD6 + sellPenaltyFeeD6 + buyFeeD6 + buyPrizePoolFeeD6;
    const estimatedTokensOutD6 = buyResult.value.venue === "bonding_curve" ? buyResult.value.outcomeAmountOutD6 : buyResult.value.outputAmountD6;
    const buyAmountInD6 = buyResult.value.venue === "bonding_curve" ? buyResult.value.quoteToCurveD6 : buyResult.value.inputAmountD6;
    const buyWalletDebitD6 = buyResult.value.venue === "bonding_curve" ? buyResult.value.totalQuoteDebitD6 : buyResult.value.inputAmountD6;
    t30 = {
      sellGrossD6,
      sellBaseFeeD6,
      sellPrizePoolFeeD6,
      sellPenaltyFeeD6,
      buyFeeD6,
      buyPrizePoolFeeD6,
      totalCostD6,
      estimatedTokensOutD6: new BN(estimatedTokensOutD6.toString()),
      expectedSellNetUsdcOutD6: new BN(sellNetD6.toString()),
      sellGrossQuoteOutputD6: new BN(sellGrossD6.toString()),
      buyAmountInD6: new BN(buyAmountInD6.toString()),
      buyWalletDebitD6: new BN(buyWalletDebitD6.toString()),
      buyGrossQuoteInputD6: new BN(buyAmountInD6.toString())
    };
  }
  const swapFeeEstimate = t30;
  const skipStatusGate = isGraduatedAmm;
  const nonTradeableKey = marketStatus !== undefined ? getNonTradeableKey(marketStatus) : undefined;
  const hasPositiveAmount = isSwap ? swapPct > 0 : sellPercentage !== null ? percentageOutcomeAmountD6 > BigInt(0) : localAmount > 0;
  const poolFailureReason = selectedPoolContextUnavailableReason ?? (isSwap ? swapToPoolContextUnavailableReason : null);
  const poolState = marketDataStale ? {
    status: "unavailable",
    reason: "request_failed"
  } as const : poolFailureReason !== null ? {
    status: "unavailable",
    reason: poolFailureReason
  } as const : marketDataPending || tradeData === undefined ? {
    status: "loading"
  } as const : !hasReserves ? {
    status: "unavailable",
    reason: "venue_unavailable"
  } as const : blockForRefresh ? {
    status: "refreshing"
  } as const : {
    status: "ready"
  } as const;
  const walletBalanceState = deriveWalletBalanceState({
    needsLogin,
    isBuy,
    hasUsdcBalance: tradeBalancesQuery.usdcBalance !== undefined,
    hasTradeBalances: tradeBalances !== undefined,
    queryError: tradeBalancesQuery.isError,
    queryPending: tradeBalancesQuery.isPending,
    queryPlaceholder: tradeBalancesQuery.isPlaceholderData,
    queryFetching: !allowRapidTrades && tradeBalancesQuery.isFetching,
    queryStale: !allowRapidTrades && tradeBalancesQuery.isStale,
    exactSellBalanceAvailable
  });
  let validationReason;
  if (isSwap && tokenHoldingSellValueUsdc <= 0) {
    validationReason = "swap_no_holdings";
  } else {
    if (isBuy && buyEstimate !== null && balance < usdcAmount) {
      validationReason = "insufficient_balance";
    } else {
      if (isBuy && buyEstimate !== null && balance < buyEstimate.totalCost) {
        validationReason = "insufficient_for_fees";
      } else {
        if (hasNoSellableHoldings) {
          validationReason = "no_holdings";
        }
      }
    }
  }
  const exactBagQuoteReady = liveSellMaxReady && sellExceedsHoldings;
  const estimateState = !hasPositiveAmount ? {
    status: "not_requested"
  } as const : isSwap ? swapFeeEstimate === null ? {
    status: "unavailable",
    reason: "quote_rejected"
  } as const : {
    status: "ready"
  } as const : sellPercentage !== null ? percentageQuoteReady ? {
    status: "ready"
  } as const : {
    status: "unavailable",
    reason: "quote_rejected"
  } as const : estimate === null && !exactBagQuoteReady ? {
    status: "unavailable",
    reason: "quote_rejected"
  } as const : {
    status: "ready"
  } as const;
  const panelState = deriveTradePanelState({
    hasPositiveAmount,
    ...(nonTradeableKey !== undefined && !skipStatusGate ? {
      lifecycleReason: nonTradeableKey
    } : {}),
    pool: poolState,
    walletBalance: walletBalanceState,
    estimate: estimateState,
    ...(validationReason !== undefined ? {
      validationReason
    } : {})
  });
  const submitDisabled = panelState.kind !== "ready";
  const marketHash = hashDiagnosticEntity(marketAddress);
  const outcomeHash = hashDiagnosticEntity(selectedOutcome?.mint ?? `outcome-${outcomeIndex}`);
  const walletHash = hashDiagnosticEntity(activeWallet?.address ?? "signed-out");
  useEffect(() => {
    if (panelState.kind !== "unavailable") {
      return;
    }
    const key = `${marketHash}:${outcomeHash}:${walletHash}:${panelState.stage}:${panelState.reason}`;
    const loggedKeys = unavailableStateLogKeysRef.current;
    if (loggedKeys.has(key)) {
      return;
    }
    loggedKeys.add(key);
    logger.warn("trade.quote_context.unavailable", {
      stage: panelState.stage,
      reason: panelState.reason,
      marketHash,
      outcomeHash,
      walletHash
    });
  }, [marketHash, outcomeHash, panelState, walletHash]);
  const retryUnavailableState = function retryUnavailableState() {
    if (panelState.kind !== "unavailable") {
      return;
    }
    if (panelState.stage === "wallet_balance") {
      tradeBalancesQuery.refetch();
    }
    if (panelState.stage === "pool_snapshot") {
      onRetryMarketData?.();
    }
  };
  const handleSubmit = function handleSubmit() {
    if (needsLogin) {
      executor.requestLogin();
      return;
    }
    if (isTradeSubmissionBlocked(isSwap)) {
      return;
    }
    if (!activeWallet || submitDisabled || blockedWhilePending || !selectedOutcome) {
      return;
    }
    executeTrade();
  };
  let t31;
  if ($[51] !== feeSplitBackersBps) {
    t31 = function platformFeeShareD6(baseFeeD6) {
      if (feeSplitBackersBps === undefined || !Number.isSafeInteger(feeSplitBackersBps) || feeSplitBackersBps < 0 || feeSplitBackersBps > Number(BPS_DENOMINATOR)) {
        return;
      }
      return splitFloorFee(baseFeeD6, BigInt(feeSplitBackersBps), BPS_DENOMINATOR).remainderAmount.toString();
    };
    $[51] = feeSplitBackersBps;
    $[52] = t31;
  } else {
    t31 = $[52];
  }
  const platformFeeShareD6 = t31;
  function executeTrade() {
    if (!selectedOutcome || !activeWallet || marketDataStale || blockForRefresh) {
      return;
    }
    const confettiOrigin = confettiOriginFromElement(formRootRef.current) ?? undefined;
    if (isSwap) {
      if (swapToOutcome === undefined || swapFeeEstimate === null) {
        return;
      }
      const fromTokensD6 = new BN(swapOutcomeAmountInD6.toString());
      if (fromTokensD6.isZero()) {
        return;
      }
      const wallet = {
        id: activeWallet.id,
        address: activeWallet.address,
        type: activeWallet.type
      };
      executor.withTradeGate(() => {
        if (isTradeSubmissionBlocked(true)) {
          return;
        }
        swapMutation.mutate({
          marketAddress,
          fromOutcomeIndex: outcomeIndex,
          toOutcomeIndex: swapToIndex,
          fromOutcomeMint: selectedOutcome.mint,
          toOutcomeMint: swapToOutcome.mint,
          tokensD6: fromTokensD6,
          expectedSellNetUsdcOutD6: swapFeeEstimate.expectedSellNetUsdcOutD6,
          sellGrossQuoteOutputD6: swapFeeEstimate.sellGrossQuoteOutputD6,
          sellPenaltyFeeD6: new BN(swapFeeEstimate.sellPenaltyFeeD6.toString()),
          buyAmountInD6: swapFeeEstimate.buyAmountInD6,
          buyWalletDebitD6: swapFeeEstimate.buyWalletDebitD6,
          buyGrossQuoteInputD6: swapFeeEstimate.buyGrossQuoteInputD6,
          estimatedTokensOutD6: swapFeeEstimate.estimatedTokensOutD6,
          marketPhase: "bonding_curve",
          slippageBps: effectiveSlippageBps,
          executionPolicy: "atomic_then_sequential",
          ...(() => {
            const fee = platformFeeShareD6(swapFeeEstimate.sellBaseFeeD6 + swapFeeEstimate.buyFeeD6);
            return fee !== undefined ? {
              platformFeeD6: fee
            } : {};
          })(),
          activeWallet: wallet,
          signingDeps
        });
      }, confettiOrigin);
    } else {
      if (isBuy && buyEstimate) {
        executor.executeBuy({
          marketAddress,
          outcomeIndex,
          outcomeMint: selectedOutcome.mint,
          amountInD6: buyEstimate.amountInD6,
          walletDebitD6: buyEstimate.walletDebitD6,
          feeExcludedQuoteInputD6: buyEstimate.feeExcludedQuoteInputD6,
          outcomeTokensOutD6: buyEstimate.outcomeTokensOutD6,
          desiredAmountD6: buyEstimate.amountInD6,
          slippageBps: effectiveSlippageBps,
          suggestedSlippageBps: buyEstimate.suggestedSlippageBps,
          marketPhase: buyEstimate.marketPhase,
          platformFeeD6: platformFeeShareD6(BigInt(buyEstimate.buyFeeD6.toString())),
          confettiOrigin
        });
      } else {
        if (direction === "sell") {
          const needsExactSell = sellPercentage !== null || sellExceedsHoldings || sellEstimate === null || !exactSellBalanceAvailable || onchainOutcomeBalanceD6 === null || BigInt(sellEstimate.tokensNeededD6.toString()) > onchainOutcomeBalanceD6;
          if (needsExactSell && (sellPercentage === null ? tokenHoldingSellValueUsdc : percentageSellValueUsdc) > 0 && selectedPoolContext !== null && !marketDataStale && !blockForRefresh) {
            if (!exactSellBalanceAvailable || onchainOutcomeBalanceD6 === null) {
              logger.warn("trade-form: sell blocked, on-chain outcome balance not loaded", {
                market: marketAddress,
                outcomeIndex,
                sellPercentage
              });
              showToast({
                id: TOAST_IDS.SELL_UNAVAILABLE,
                message: t("validation.balance_syncing"),
                variant: "error"
              });
              return;
            }
            const clampTokensD6 = minimumBigInt(sellPercentage === null ? maximumOutcomeAmountInD6 : percentageOutcomeAmountD6, onchainOutcomeBalanceD6);
            if (clampTokensD6 <= BigInt(0)) {
              logger.warn("trade-form: sell-all clamp resolved to zero sellable tokens", {
                market: marketAddress,
                outcomeIndex,
                sellPercentage
              });
              showToast({
                id: TOAST_IDS.SELL_UNAVAILABLE,
                message: t("validation.no_holdings"),
                variant: "error"
              });
              return;
            }
            const tokensD6 = new BN(clampTokensD6.toString());
            const sellAllResult = quoteOutcomeSellExactIn({
              context: selectedPoolContext,
              outcomeAmountInD6: BigInt(tokensD6.toString())
            });
            if (!sellAllResult.available) {
              logger.warn("trade-form: full-position SDK quote unavailable", {
                market: marketAddress,
                outcomeIndex,
                sellPercentage,
                reason: sellAllResult.reason
              });
              showToast({
                id: TOAST_IDS.SELL_UNAVAILABLE,
                message: t("validation.quote_unavailable"),
                variant: "error"
              });
              return;
            }
            const expectedNetD6 = sellAllResult.value.venue === "bonding_curve" ? sellAllResult.value.netQuoteOutputD6 : sellAllResult.value.outputAmountD6;
            const grossD6 = sellAllResult.value.venue === "bonding_curve" ? sellAllResult.value.grossQuoteOutputD6 : sellAllResult.value.curveOutputAmountD6;
            const feeExcludedQuote = quoteOutcomeSellFeeExcludedExactIn({
              context: selectedPoolContext,
              outcomeAmountInD6: BigInt(tokensD6.toString())
            });
            if (!feeExcludedQuote.available) {
              logger.warn("trade-form: fee-excluded full-position quote unavailable", {
                market: marketAddress,
                outcomeIndex,
                sellPercentage,
                reason: feeExcludedQuote.reason
              });
              showToast({
                id: TOAST_IDS.SELL_UNAVAILABLE,
                message: t("validation.quote_unavailable"),
                variant: "error"
              });
              return;
            }
            executor.executeSell({
              marketAddress,
              marketTitle,
              outcomeIndex,
              outcomeMint: selectedOutcome.mint,
              outcomeLabel: selectedOutcome.label,
              tokensNeededD6: tokensD6,
              expectedNetUsdcOutD6: new BN(expectedNetD6.toString()),
              grossQuoteAmountD6: new BN(grossD6.toString()),
              feeExcludedQuoteOutputD6: new BN(feeExcludedQuote.value.toString()),
              sellPenaltyFeeD6: new BN((sellAllResult.value.venue === "bonding_curve" ? sellAllResult.value.sellPenaltyFeeD6 : BigInt(0)).toString()),
              positionAccounting: sellPositionAccounting,
              slippageBps: effectiveSlippageBps,
              suggestedSlippageBps: estimate?.suggestedSlippageBps ?? 100,
              marketPhase: sellAllResult.value.venue === "cp_swap" ? "amm" : "bonding_curve",
              platformFeeD6: platformFeeShareD6(sellAllResult.value.venue === "bonding_curve" ? sellAllResult.value.sellBaseFeeD6 : BigInt(0)),
              confettiOrigin
            });
          } else {
            if (sellEstimate !== null) {
              executor.executeSell({
                marketAddress,
                marketTitle,
                outcomeIndex,
                outcomeMint: selectedOutcome.mint,
                outcomeLabel: selectedOutcome.label,
                tokensNeededD6: sellEstimate.tokensNeededD6,
                expectedNetUsdcOutD6: sellEstimate.expectedNetUsdcOutD6,
                grossQuoteAmountD6: sellEstimate.grossQuoteAmountD6,
                feeExcludedQuoteOutputD6: sellEstimate.feeExcludedQuoteOutputD6,
                sellPenaltyFeeD6: sellEstimate.penaltyFeeD6,
                positionAccounting: sellPositionAccounting,
                slippageBps: effectiveSlippageBps,
                suggestedSlippageBps: sellEstimate.suggestedSlippageBps,
                marketPhase: sellEstimate.marketPhase,
                platformFeeD6: platformFeeShareD6(BigInt(sellEstimate.sellBaseFeeD6.toString())),
                confettiOrigin
              });
            }
          }
        }
      }
    }
  }
  const handleQuickAmount = function handleQuickAmount(quickAmount) {
    if (isSellTab) {
      selectSellPercentage(quickAmount);
    } else {
      setAmountStr(String(quickAmount));
    }
  };
  function selectSellPercentage(percentage) {
    if (![25, 50, 75, 100].includes(percentage) || selectedPoolContext === null || !exactSellBalanceAvailable || maximumOutcomeAmountInD6 <= BigInt(0) || marketDataStale || blockForRefresh) {
      return;
    }
    const outcomeAmountInD6 = maximumOutcomeAmountInD6 * BigInt(percentage) / BigInt(100);
    const quote = quoteOutcomeSellExactIn({
      context: selectedPoolContext,
      outcomeAmountInD6
    });
    const grossD6_0 = quote.available ? sellDisplayEstimateFromQuote(quote.value).grossQuoteAmountD6 : BigInt(0);
    const localValue = floorDisplayAmount((boundedEditableUsdcFromD6(grossD6_0) ?? 0) * exchangeRate, currencyDecimals);
    setSellAmount({
      value: String(localValue),
      sellPercentage: {
        outcomeIndex,
        percentage
      }
    });
  }
  const handleMaxClick = function handleMaxClick() {
    if (isBuy) {
      if (balance <= 0 || selectedPoolContext === null || marketDataStale || blockForRefresh) {
        return;
      }
      const budgetD6 = usdcToD6(balance);
      const quote_0 = quoteOutcomeBuyForQuoteBudget({
        context: selectedPoolContext,
        maximumQuoteDebitD6: BigInt(budgetD6.toString())
      });
      if (!quote_0.available) {
        return;
      }
      const amountInD6 = new BN((quote_0.value.venue === "bonding_curve" ? quote_0.value.quoteToCurveD6 : quote_0.value.inputAmountD6).toString());
      const maxUsdc = d6ToUsdc(amountInD6);
      const maxLocal = Math.floor(maxUsdc * exchangeRate * 100) / 100;
      setAmountStr(String(Math.max(0, maxLocal)));
    } else {
      if (isDust(tokenHoldingSellValueUsdc) || selectedPoolContext === null || marketDataStale || blockForRefresh) {
        return;
      }
      selectSellPercentage(100);
    }
  };
  const handleMaxClickRef = useRef(handleMaxClick);
  useLayoutEffect(() => {
    handleMaxClickRef.current = handleMaxClick;
  });
  useEffect(() => {
    if (pendingMaxNonceRef.current === null) {
      return;
    }
    if (tradeDirRequest === null) {
      return;
    }
    if (pendingMaxNonceRef.current !== tradeDirRequest.nonce) {
      return;
    }
    if (direction !== tradeDirRequest.direction) {
      return;
    }
    if (direction === "sell") {
      if (isDust(tokenHoldingSellValueUsdc) || isDust(tokenHoldingBalance) || !hasReserves) {
        return;
      }
    } else {
      if (direction === "buy") {
        if (isDust(balance)) {
          return;
        }
      }
    }
    pendingMaxNonceRef.current = null;
    handleMaxClickRef.current();
  }, [tradeDirRequest, direction, tokenHoldingSellValueUsdc, tokenHoldingBalance, hasReserves, balance]);
  let t32;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = function handleSwapPctChange(val) {
      if (/^\d{0,3}$/.test(val)) {
        const num = Number.parseInt(val, 10);
        if (val === "" || num >= 0 && num <= 100) {
          setSwapPctStr(val);
        }
      }
    };
    $[53] = t32;
  } else {
    t32 = $[53];
  }
  const handleSwapPctChange = t32;
  let t33;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = function handleSwapQuickAmount(pct) {
      setSwapPctStr(String(pct));
    };
    $[54] = t33;
  } else {
    t33 = $[54];
  }
  const handleSwapQuickAmount = t33;
  let t34;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = function handleSwapMaxClick() {
      setSwapPctStr("100");
    };
    $[55] = t34;
  } else {
    t34 = $[55];
  }
  const handleSwapMaxClick = t34;
  let t35;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = [{
      label: "10%",
      value: 10
    }, {
      label: "25%",
      value: 25
    }, {
      label: "50%",
      value: 50
    }, {
      label: "75%",
      value: 75
    }];
    $[56] = t35;
  } else {
    t35 = $[56];
  }
  const swapQuickAmounts = t35;
  const t36 = balance * exchangeRate;
  let t37;
  if ($[57] !== t36) {
    t37 = {
      maxLocal: t36
    };
    $[57] = t36;
    $[58] = t37;
  } else {
    t37 = $[58];
  }
  const buyQuickAmounts = useQuickAmounts(t37);
  let t38;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = [{
      label: "25%",
      value: 25
    }, {
      label: "50%",
      value: 50
    }, {
      label: "75%",
      value: 75
    }];
    $[59] = t38;
  } else {
    t38 = $[59];
  }
  const sellQuickAmounts = t38;
  const quickAmounts = isSellTab ? sellQuickAmounts : buyQuickAmounts;
  const outcomeLabel = selectedOutcome?.label ?? "";
  const swapToLabel = swapToOutcome?.label ?? "";
  let t39;
  if ($[60] !== lockOutcome || $[61] !== outcomes.length) {
    t39 = outcomes.length > 1 && !lockOutcome && checkGate("swap_trading");
    $[60] = lockOutcome;
    $[61] = outcomes.length;
    $[62] = t39;
  } else {
    t39 = $[62];
  }
  const showSwapTab = t39;
  let buyShareCountD6;
  let t40;
  if ($[63] !== buyEstimate || $[64] !== formatD9) {
    buyShareCountD6 = buyEstimate !== null ? BigInt(buyEstimate.outcomeTokensOutD6.toString()) : null;
    const buyAveragePriceD9 = buyEstimate !== null && buyShareCountD6 !== null ? effectiveExecutionPriceD9OrNull(BigInt(buyEstimate.walletDebitD6.toString()), buyShareCountD6) : null;
    t40 = buyAveragePriceD9 !== null ? formatD9(buyAveragePriceD9.toString()) : null;
    $[63] = buyEstimate;
    $[64] = formatD9;
    $[65] = buyShareCountD6;
    $[66] = t40;
  } else {
    buyShareCountD6 = $[65];
    t40 = $[66];
  }
  const buyAveragePriceLabel = t40;
  const sellShareCountD6 = displayedSellEstimate !== null ? sellPercentage !== null ? percentageOutcomeAmountD6 : sellExceedsHoldings ? maximumOutcomeAmountInD6 : sellEstimate !== null ? BigInt(sellEstimate.tokensNeededD6.toString()) : null : null;
  const sellAveragePriceD9 = displayedSellEstimate !== null && sellShareCountD6 !== null ? effectiveExecutionPriceD9OrNull(displayedSellEstimate.netUsdcReceivedD6, sellShareCountD6) : null;
  const sellAveragePriceLabel = sellAveragePriceD9 !== null ? formatD9(sellAveragePriceD9.toString()) : null;
  const submitLabel = needsLogin ? t("validation.connect_wallet") : isSwap ? t("swap_outcome", {
    outcome: swapToLabel
  }) : isBuy ? t("buy_outcome", {
    outcome: outcomeLabel
  }) : sellAllSelected || sellExceedsHoldings ? t("sell_all_outcome", {
    outcome: outcomeLabel
  }) : t("sell_outcome", {
    outcome: outcomeLabel
  });
  let t41;
  bb1: {
    if (panelState.kind === "loading") {
      t41 = panelState.stage === "pool_snapshot" ? t("status.loading_pool") : t("status.loading_balance");
      break bb1;
    }
    if (panelState.kind === "refreshing") {
      let t42;
      if ($[67] !== t) {
        t42 = t("status.refreshing_quote");
        $[67] = t;
        $[68] = t42;
      } else {
        t42 = $[68];
      }
      t41 = t42;
      break bb1;
    }
    if (panelState.kind === "validation_blocked") {
      switch (panelState.reason) {
        case "insufficient_balance":
          {
            let t42;
            if ($[69] !== t) {
              t42 = t("validation.insufficient_balance");
              $[69] = t;
              $[70] = t42;
            } else {
              t42 = $[70];
            }
            t41 = t42;
            break bb1;
          }
        case "insufficient_for_fees":
          {
            let t42;
            if ($[71] !== t) {
              t42 = t("validation.insufficient_for_fees");
              $[71] = t;
              $[72] = t42;
            } else {
              t42 = $[72];
            }
            t41 = t42;
            break bb1;
          }
        case "market_backing":
          {
            let t42;
            if ($[73] !== t) {
              t42 = t("validation.market_backing");
              $[73] = t;
              $[74] = t42;
            } else {
              t42 = $[74];
            }
            t41 = t42;
            break bb1;
          }
        case "market_created":
          {
            let t42;
            if ($[75] !== t) {
              t42 = t("validation.market_created");
              $[75] = t;
              $[76] = t42;
            } else {
              t42 = $[76];
            }
            t41 = t42;
            break bb1;
          }
        case "market_graduated":
          {
            let t42;
            if ($[77] !== t) {
              t42 = t("validation.market_graduated");
              $[77] = t;
              $[78] = t42;
            } else {
              t42 = $[78];
            }
            t41 = t42;
            break bb1;
          }
        case "market_resolved":
          {
            let t42;
            if ($[79] !== t) {
              t42 = t("validation.market_resolved");
              $[79] = t;
              $[80] = t42;
            } else {
              t42 = $[80];
            }
            t41 = t42;
            break bb1;
          }
        case "no_holdings":
          {
            let t42;
            if ($[81] !== t) {
              t42 = t("validation.no_holdings");
              $[81] = t;
              $[82] = t42;
            } else {
              t42 = $[82];
            }
            t41 = t42;
            break bb1;
          }
        case "swap_no_holdings":
          {
            let t42;
            if ($[83] !== t) {
              t42 = t("swap_no_holdings");
              $[83] = t;
              $[84] = t42;
            } else {
              t42 = $[84];
            }
            t41 = t42;
            break bb1;
          }
      }
    }
    if (panelState.kind === "unavailable") {
      if (panelState.stage === "pool_snapshot") {
        let t42;
        if ($[85] !== t) {
          t42 = t("status.pool_unavailable");
          $[85] = t;
          $[86] = t42;
        } else {
          t42 = $[86];
        }
        t41 = t42;
        break bb1;
      }
      if (panelState.stage === "wallet_balance") {
        let t42;
        if ($[87] !== t) {
          t42 = t("status.balance_unavailable");
          $[87] = t;
          $[88] = t42;
        } else {
          t42 = $[88];
        }
        t41 = t42;
        break bb1;
      }
      let t42;
      if ($[89] !== t) {
        t42 = t("status.quote_unavailable");
        $[89] = t;
        $[90] = t42;
      } else {
        t42 = $[90];
      }
      t41 = t42;
      break bb1;
    }
    t41 = null;
  }
  const panelStatusMessage = t41;
  const showPanelStatus = !needsLogin && panelStatusMessage !== null;
  const panelStatusAction = showPanelStatus && panelState.kind === "unavailable" && panelState.stage !== "estimate" ? <AnimatedButton type="button" variant="tertiary" size="xs" className="shrink-0" onClick={retryUnavailableState}>{t("status.retry")}</AnimatedButton> : null;
  let t42;
  if ($[91] !== feeSplitBackersBps || $[92] !== formatD6 || $[93] !== t) {
    t42 = function exactBaseTradeFeeLines(baseFeeD6_0, fallbackLabel, isNegative) {
      if (feeSplitBackersBps === undefined || !Number.isSafeInteger(feeSplitBackersBps) || feeSplitBackersBps < 0 || feeSplitBackersBps > Number(BPS_DENOMINATOR)) {
        return [{
          label: fallbackLabel,
          value: formatD6(baseFeeD6_0.toString()),
          isNegative
        }];
      }
      const split = splitFloorFee(baseFeeD6_0, BigInt(feeSplitBackersBps), BPS_DENOMINATOR);
      return [{
        label: t("fees.backer_fee"),
        value: formatD6(split.ratedAmount.toString()),
        isNegative
      }, {
        label: t("fees.platform_fee"),
        value: formatD6(split.remainderAmount.toString()),
        isNegative
      }];
    };
    $[91] = feeSplitBackersBps;
    $[92] = formatD6;
    $[93] = t;
    $[94] = t42;
  } else {
    t42 = $[94];
  }
  const exactBaseTradeFeeLines = t42;
  const buildFeeItems = function buildFeeItems() {
    if (isBuy && buyEstimate) {
      const usedAmmFees = buyEstimate.marketPhase === "amm";
      if (usedAmmFees) {
        return [{
          label: t("fees.amount_received"),
          value: formatD6(buyEstimate.amountInD6.toString())
        }, {
          label: t("fees.amm_quote_input_fee"),
          value: formatD6(buyEstimate.quoteInputFeeD6.toString(), "price"),
          isNegative: true
        }, ...(buyEstimate.outcomeOutputCreatorFeeD6.isZero() ? [] : [{
          label: t("fees.amm_outcome_output_creator_fee", {
            outcome: outcomeLabel
          }),
          value: t("shares_value", {
            count: formatOutcomeTokenD6(BigInt(buyEstimate.outcomeOutputCreatorFeeD6.toString()))
          }),
          isNegative: true
        }])];
      }
      return [{
        label: t("fees.amount_received"),
        value: formatD6(buyEstimate.amountInD6.toString())
      }, ...exactBaseTradeFeeLines(BigInt(buyEstimate.buyFeeD6.toString()), t("fees.buy_fee"), false), {
        label: t("fees.prize_pool_fee"),
        value: formatD6(buyEstimate.prizePoolFeeD6.toString())
      }];
    }
    if (isSwap && swapFeeEstimate !== null) {
      const totalBaseFeeD6 = swapFeeEstimate.sellBaseFeeD6 + swapFeeEstimate.buyFeeD6;
      const totalPrizeD6 = swapFeeEstimate.sellPrizePoolFeeD6 + swapFeeEstimate.buyPrizePoolFeeD6;
      return [{
        label: t("fees.sell_value"),
        value: formatD6(swapFeeEstimate.sellGrossD6.toString())
      }, ...exactBaseTradeFeeLines(totalBaseFeeD6, t("fees.sell_fee"), true), {
        label: t("fees.prize_pool_fee"),
        value: formatD6(totalPrizeD6.toString()),
        isNegative: true
      }, ...(swapFeeEstimate.sellPenaltyFeeD6 > BigInt(0) ? [{
        label: t("fees.penalty_fee"),
        value: formatD6(swapFeeEstimate.sellPenaltyFeeD6.toString()),
        isNegative: true
      }] : [])];
    }
    if (!isBuy && !isSwap && displayedSellEstimate) {
      const usedAmmFees_0 = displayedSellEstimate.marketPhase === "amm";
      if (usedAmmFees_0) {
        return [{
          label: t("fees.sell_value"),
          value: formatD6(displayedSellEstimate.grossQuoteAmountD6.toString())
        }, {
          label: t("fees.amm_outcome_input_fee"),
          value: t("shares_value", {
            count: formatOutcomeTokenD6(displayedSellEstimate.outcomeInputFeeD6)
          }),
          isNegative: true
        }, ...(displayedSellEstimate.quoteOutputCreatorFeeD6 === BigInt(0) ? [] : [{
          label: t("fees.amm_quote_output_creator_fee"),
          value: formatD6(displayedSellEstimate.quoteOutputCreatorFeeD6.toString(), "price"),
          isNegative: true
        }])];
      }
      return [{
        label: t("fees.sell_value"),
        value: formatD6(displayedSellEstimate.grossQuoteAmountD6.toString())
      }, ...exactBaseTradeFeeLines(displayedSellEstimate.baseFeeD6, t("fees.sell_fee"), true), {
        label: t("fees.prize_pool_fee"),
        value: formatD6(displayedSellEstimate.prizePoolFeeD6.toString()),
        isNegative: true
      }, ...(displayedSellEstimate.isPenalty && displayedSellEstimate.penaltyFeeD6 > BigInt(0) ? [{
        label: t("fees.penalty_fee"),
        value: formatD6(displayedSellEstimate.penaltyFeeD6.toString()),
        isNegative: true
      }] : [])];
    }
    return [];
  };
  let t43;
  if ($[95] !== direction) {
    t43 = v => {
      const next = v as 'buy' | 'sell' | 'swap';
      if (next !== direction) {
        setAnimateFormChanges(true);
      }
      setDirection(next);
    };
    $[95] = direction;
    $[96] = t43;
  } else {
    t43 = $[96];
  }
  let t44;
  if ($[97] !== t) {
    t44 = t("direction_buy");
    $[97] = t;
    $[98] = t44;
  } else {
    t44 = $[98];
  }
  let t45;
  if ($[99] !== t44) {
    t45 = <TabsTrigger value="buy">{t44}</TabsTrigger>;
    $[99] = t44;
    $[100] = t45;
  } else {
    t45 = $[100];
  }
  let t46;
  if ($[101] !== t) {
    t46 = t("direction_sell");
    $[101] = t;
    $[102] = t46;
  } else {
    t46 = $[102];
  }
  let t47;
  if ($[103] !== t46) {
    t47 = <TabsTrigger value="sell">{t46}</TabsTrigger>;
    $[103] = t46;
    $[104] = t47;
  } else {
    t47 = $[104];
  }
  let t48;
  if ($[105] !== showSwapTab || $[106] !== t) {
    t48 = showSwapTab && <TabsTrigger value="swap">{t("direction_swap")}</TabsTrigger>;
    $[105] = showSwapTab;
    $[106] = t;
    $[107] = t48;
  } else {
    t48 = $[107];
  }
  let t49;
  if ($[108] !== t45 || $[109] !== t47 || $[110] !== t48) {
    t49 = <TabsList variant="segmented" size="md">{t45}{t47}{t48}</TabsList>;
    $[108] = t45;
    $[109] = t47;
    $[110] = t48;
    $[111] = t49;
  } else {
    t49 = $[111];
  }
  let t50;
  if ($[112] !== direction || $[113] !== t43 || $[114] !== t49) {
    t50 = <Tabs className="min-w-0 flex-1" value={direction} onValueChange={t43}>{t49}</Tabs>;
    $[112] = direction;
    $[113] = t43;
    $[114] = t49;
    $[115] = t50;
  } else {
    t50 = $[115];
  }
  let t51;
  if ($[116] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = <TradeSettingsDialog />;
    $[116] = t51;
  } else {
    t51 = $[116];
  }
  let t52;
  if ($[117] !== t50) {
    t52 = <div className="flex w-full items-center gap-2">{t50}{t51}</div>;
    $[117] = t50;
    $[118] = t52;
  } else {
    t52 = $[118];
  }
  const t53 = isSwap ? <div className="relative rounded-control border bg-background-secondary py-2 px-1 dark:bg-transparent"><div className="flex flex-col pb-2"><span className="shrink-0 text-caption font-normal text-foreground-tertiary ml-2.5">{t("swap_from_label")}</span><OutcomePills marketPubkey={marketAddress} outcomes={outcomes} selectedIndex={outcomeIndex} onSelect={setOutcomeIndex} borderless={true} {...visibleOutcomeBalances === undefined ? {} : {
        outcomeBalances: visibleOutcomeBalances
      }} /></div><div className="relative z-10 flex h-0 items-center justify-center"><button type="button" aria-label={`${t("swap_from_label")} / ${t("swap_to_label")}`} onClick={() => {
        const prevFrom = outcomeIndex;
        const prevTo = swapToIndex;
        setOutcomeIndex(prevTo);
        setSwapToOutcomeIndex(prevFrom);
      }} className="flex size-7 items-center justify-center rounded-full border bg-background-secondary text-foreground-secondary shadow-elevation-inset transition-[background-color,color,transform] hover:bg-background-tertiary hover:text-foreground-primary active:scale-95"><Icon icon={ArrowDataTransferVerticalIcon} className="size-3.5" /></button></div><div className="border-t" /><div className="flex flex-col pt-2"><span className="shrink-0 text-caption font-normal text-foreground-tertiary ml-2.5">{t("swap_to_label")}</span><OutcomePills marketPubkey={marketAddress} outcomes={outcomes.filter(o_3 => o_3.index !== outcomeIndex)} selectedIndex={swapToIndex} onSelect={setSwapToOutcomeIndex} borderless={true} {...visibleOutcomeBalances === undefined ? {} : {
        outcomeBalances: visibleOutcomeBalances
      }} /></div></div> : <OutcomePills marketPubkey={marketAddress} outcomes={lockOutcome && selectedOutcome !== undefined ? [selectedOutcome] : outcomes} selectedIndex={outcomeIndex} onSelect={setOutcomeIndex} {...visibleOutcomeBalances === undefined ? {} : {
    outcomeBalances: visibleOutcomeBalances
  }} triggerClassName="bg-background-secondary dark:bg-transparent" />;
  let t54;
  if ($[119] !== t53) {
    t54 = <div className="min-w-0">{t53}</div>;
    $[119] = t53;
    $[120] = t54;
  } else {
    t54 = $[120];
  }
  let t55;
  if ($[121] !== isSwap) {
    t55 = isSwap ? {
      suffixSymbol: "%"
    } : {};
    $[121] = isSwap;
    $[122] = t55;
  } else {
    t55 = $[122];
  }
  let t56;
  if ($[123] !== isSwap || $[124] !== t) {
    t56 = isSwap ? t("swap_percentage_label") : t("amount_input_label");
    $[123] = isSwap;
    $[124] = t;
    $[125] = t56;
  } else {
    t56 = $[125];
  }
  let t57;
  if ($[126] !== t) {
    t57 = t("max_button");
    $[126] = t;
    $[127] = t57;
  } else {
    t57 = $[127];
  }
  const T0 = AnimatedButton;
  const t58 = needsLogin ? "primary" : "tertiary";
  const t59 = true;
  const t60 = true;
  const t61 = cn("w-full border border-transparent text-body-lg font-medium", !needsLogin && !submitDisabled && !isSwap && isGraduatedAmm && graduatedAmmSubmitColor, !needsLogin && !submitDisabled && !isSwap && !isGraduatedAmm && selectedOutcomeColor !== undefined && cn(selectedOutcomeColor.fill, selectedOutcomeColor.hoverOutline, selectedOutcomeColor.text), !needsLogin && !submitDisabled && isSwap && "bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700", !needsLogin && panelState.kind === "refreshing" && "disabled:opacity-100", !needsLogin && submitDisabled && "bg-background-tertiary text-foreground-tertiary disabled:opacity-100");
  const t62 = !needsLogin && (submitDisabled || blockedWhilePending);
  const t63 = !needsLogin && blockedWhilePending;
  let t64;
  if ($[128] !== T0 || $[129] !== handleSubmit || $[130] !== submitLabel || $[131] !== t58 || $[132] !== t61 || $[133] !== t62 || $[134] !== t63) {
    t64 = <T0 variant={t58} disableHoverScale={t59} disableTapScale={t60} className={t61} size="xl" disabled={t62} loading={t63} onClick={handleSubmit}>{submitLabel}</T0>;
    $[128] = T0;
    $[129] = handleSubmit;
    $[130] = submitLabel;
    $[131] = t58;
    $[132] = t61;
    $[133] = t62;
    $[134] = t63;
    $[135] = t64;
  } else {
    t64 = $[135];
  }
  const formContent = <div ref={formRootRef} className="flex flex-col gap-3">{t52}<TradeFormTransition direction={direction} animateChanges={animateFormChanges} bare={bare}>{t54}{isBuy || isSellTab ? <DetailWalletPicker balanceLabel={needsLogin || tradeBalances === undefined ? formatBalance(0) : formatBalance(balance)} fallbackLabel={t("balance_label")} disabled={isTradePending} /> : isSwap ? <div className="flex flex-col gap-1"><div className="flex items-center justify-between pl-1"><div className="flex min-h-6 items-center gap-2 text-body text-foreground-secondary"><Icon icon={RepeatIcon} className="size-3.5" /><span>{t("swap_value_label")}</span></div><span className="text-body font-medium tabular-nums text-foreground-primary">{swapFeeEstimate === null ? formatD6("0") : formatD6(swapFeeEstimate.sellGrossD6.toString())}</span></div></div> : null}<div className="flex flex-col gap-3"><AnimatedAmountInput value={isSwap ? swapPctStr : displayedAmountStr} onChange={isSwap ? handleSwapPctChange : setAmountStr} currencySymbol={isSwap ? "" : currencySymbol} {...t55} quickAmounts={isSwap ? swapQuickAmounts : quickAmounts} onQuickAmount={isSwap ? handleSwapQuickAmount : handleQuickAmount} onMaxClick={isSwap ? handleSwapMaxClick : handleMaxClick} onSubmit={needsLogin || !submitDisabled && !blockedWhilePending ? handleSubmit : undefined} inputLabel={t56} maxLabel={t57} selectedQuickAmount={isSellTab ? sellAllSelected ? "max" : sellPercentage : undefined} quickAmountsDisabled={isSellTab && (hasNoSellableHoldings || !exactSellBalanceAvailable || needsLogin)} {...showPanelStatus || isBuy || isSellTab ? {
          belowInput: <ReceiveSharesRow shareCountD6={isBuy ? buyShareCountD6 : isSellTab ? sellShareCountD6 : null} averagePriceLabel={isBuy ? buyAveragePriceLabel : isSellTab ? sellAveragePriceLabel : null} statusMessage={showPanelStatus ? panelStatusMessage : null} statusRole={panelState.kind === "unavailable" ? "alert" : "status"} statusTone={panelState.kind === "unavailable" ? "error" : "neutral"} statusAction={panelStatusAction} />
        } : {}} />{!isSwap && <TradeDetails onExpandedChange={() => setAnimateFormChanges(true)} feesRowHidden={isBuy ? buyEstimate === null : displayedSellEstimate === null} loading={panelState.kind === "loading" && hasPositiveAmount} items={(isBuy ? buyEstimate !== null : displayedSellEstimate !== null) ? buildFeeItems() : []} totalLabel={isBuy ? t("total_cost_label") : t("you_receive_label")} totalValue={isBuy && buyEstimate ? formatD6(buyEstimate.walletDebitD6.toString()) : displayedSellEstimate ? formatD6(displayedSellEstimate.netUsdcReceivedD6.toString()) : formatD6("0")} feeKind={isAmm ? "winner" : "trade"} />}{isSwap && <TradeDetails onExpandedChange={() => setAnimateFormChanges(true)} feesRowHidden={swapFeeEstimate === null} loading={panelState.kind === "loading" && hasPositiveAmount} items={swapFeeEstimate !== null ? buildFeeItems() : []} totalLabel={t("fees.total_fees_label")} totalValue={swapFeeEstimate !== null ? formatD6(swapFeeEstimate.totalCostD6.toString()) : formatD6("0")} {...swapFeeEstimate !== null ? {
          infoNote: t("fees.swap_info")
        } : {}} />}{t64}</div></TradeFormTransition></div>;
  return {
    formContent,
    bare
  };
}
function _temp3(current) {
  return {
    ...current,
    sellPercentage: null
  };
}
function _temp2(row) {
  return [row.outcomeIndex, row];
}
function _temp(o) {
  return {
    title: o.label,
    index: o.index
  };
}
function TradeFormInternal(props) {
  const $ = _c(3);
  const {
    formContent,
    bare
  } = useTradeForm(props);
  let t0;
  if ($[0] !== bare || $[1] !== formContent) {
    t0 = bare ? formContent : <Panel className="w-full max-w-md overflow-hidden"><PanelContent className="px-4">{formContent}</PanelContent></Panel>;
    $[0] = bare;
    $[1] = formContent;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}

// ---------------------------------------------------------------------------
// Public surface — layout variants × mode variants (spec 055 / US6)
// ---------------------------------------------------------------------------
//
// LAYOUT VARIANTS — pick the wrapping chrome:
//
//   <TradeFormPanel>  — wraps the form in a `<Panel>` (default sidebar surface).
//                       Alias: <TradeForm> (kept for backward-compat).
//   <TradeFormBare>   — no Panel wrapper; for use inside sheets/modals/dialogs
//                       that already provide their own surface chrome.
//
// MODE VARIANTS — make the trade-mode intent explicit at the call site. Both
// resolve to the same internal orchestrator (`TradeFormInternal`) which derives
// the active mode from the selected outcome's indexed pool state on every render —
// the unified component is intentional because users can switch outcomes
// mid-form and the trade mode has to follow the selection. Use whichever name
// reads best at the consumer (we render `<BondingCurveTradeForm>` from a
// market-detail callsite that knows the selected outcome is on the curve, and
// `<AmmTradeForm>` once that outcome has graduated to the AMM pool).
//
//   <BondingCurveTradeForm>  — explicit "bonding-curve trade" intent
//   <AmmTradeForm>           — explicit "AMM swap" intent
//
// MODE × LAYOUT: layout variants accept `mode` children when needed; for the
// common cases consumers can use `<TradeFormPanel>` / `<TradeFormBare>`
// directly without naming the mode (it's data-derived).

/**
 * Panel-wrapped trade form — default surface for the market-detail sidebar.
 * The trade mode (BC vs AMM) is derived from the selected outcome's pool state.
 */
export function TradeFormPanel(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <TradeFormInternal {...props} bare={false} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}

/** @deprecated Prefer `<TradeFormPanel>` (same behaviour, clearer name). */
export function TradeForm(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <TradeFormPanel {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}

/**
 * Bare trade form — for use inside sheets/modals that already provide their
 * own surface chrome. Replaces the legacy `<TradeForm bare>` boolean prop.
 */
export function TradeFormBare(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <TradeFormInternal {...props} bare={true} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}

/**
 * Bonding-curve trade form (mode variant). Use at callsites where the
 * selected outcome is on the curve. Renders `<TradeFormPanel>` underneath —
 * mode-specific logic (2000 bps slippage cap, BC-only reserve fields,
 * `marketPhase: 'bonding_curve'` on submit-event payloads) flows from the
 * indexed pool-state snapshot.
 */
export function BondingCurveTradeForm(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <TradeFormInternal {...props} bare={false} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}

/**
 * AMM trade form (mode variant). Use at callsites where the selected outcome
 * has graduated to the AMM pool. Renders `<TradeFormPanel>` underneath;
 * AMM-specific logic (500 bps slippage cap and `marketPhase: 'amm'` on
 * submit-event payloads) flows from the indexed pool-state snapshot.
 */
export function AmmTradeForm(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <TradeFormInternal {...props} bare={false} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}